import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import useLocales from 'hooks/useLocales';
import Image from 'components/image/Image';
import Button from 'components/Button/Index';
import { getDateFormatter } from 'helpers/dateConverter';
import { monthlySalary } from 'core/constants/preferenceConstats';
import HtmlContent from 'components/HtmlContent';

import './Index.scss';
import useAuth from 'hooks/useAuth';

const DESCRIPTION_MAX_HEIGHT = 120;
const CATEGORIES_MAX_HEIGHT = 122;

const JobOpeningCard = ({ item }: any): JSX.Element => {
  const { isCandidate } = useAuth();
  const { t } = useLocales();
  const navigate = useNavigate();
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const [descriptionCanExpand, setDescriptionCanExpand] = useState(false);
  const [categoriesCanExpand, setCategoriesCanExpand] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);

  useEffect(() => {
    setDescriptionExpanded(false);
    setCategoriesExpanded(false);
  }, [item?.id, item?.description, item?.competencies]);

  useEffect(() => {
    const checkOverflow = () => {
      if (descriptionRef.current) {
        setDescriptionCanExpand(
          descriptionRef.current.scrollHeight > DESCRIPTION_MAX_HEIGHT
        );
      }
      if (categoriesRef.current) {
        setCategoriesCanExpand(
          categoriesRef.current.scrollHeight > CATEGORIES_MAX_HEIGHT
        );
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [item?.competency_labels, item?.competencies, item?.description]);

  const toggleDescriptionExpanded = () => {
    setDescriptionExpanded((prev) => !prev);
  };

  const toggleCategoriesExpanded = () => {
    setCategoriesExpanded((prev) => !prev);
  };

  return (
    <div className="jobOpeningCart">
      <div className="cartBody">
        <p className="typeText">
          {item?.is_remote && 'Remote'}{' '}
          {item?.is_remote && item?.if_office_work && ','}{' '}
          {item?.if_office_work && ' Office work'}
        </p>
        <p className="typeTitle" style={{ marginBottom: '4px' }}>{item?.title}</p>
        {item?.created_at && (
          <p className="typePosted" style={{ marginBottom: '8px' }}>
            {getDateFormatter(item.created_at, 'DD MMM YYYY')}
          </p>
        )}
        <div className="description-container">
          <p
            className={`typeDescription ${
              descriptionExpanded ? 'is-expanded' : ''
            } ${descriptionCanExpand && !descriptionExpanded ? 'can-expand' : ''}`}
            ref={descriptionRef}
            onClick={() => {
              if (descriptionCanExpand && !descriptionExpanded) {
                setDescriptionExpanded(true);
              }
            }}
            onKeyDown={(event) => {
              if (
                descriptionCanExpand &&
                !descriptionExpanded &&
                (event.key === 'Enter' || event.key === ' ')
              ) {
                event.preventDefault();
                setDescriptionExpanded(true);
              }
            }}
            role={descriptionCanExpand && !descriptionExpanded ? 'button' : undefined}
            tabIndex={descriptionCanExpand && !descriptionExpanded ? 0 : undefined}
          >
            <HtmlContent
              content={item?.description || ''}
              className="jobdescription__publicjobs"
            />
          </p>
          {(descriptionCanExpand || descriptionExpanded) && (
            <button
              type="button"
              className="scroll-indicator"
              onClick={toggleDescriptionExpanded}
            >
              <Image className="arrow-down" name="down-arrow.svg" />
              <span className="scroll-indicator-text">
                {descriptionExpanded
                  ? t('Click to collapse')
                  : t('Scroll or click to expand')}
              </span>
            </button>
          )}
        </div>
        <div className="categories-container">
          <div
            className={`categories ${
              categoriesExpanded ? 'is-expanded' : ''
            } ${categoriesCanExpand && !categoriesExpanded ? 'can-expand' : ''}`}
            ref={categoriesRef}
            onClick={() => {
              if (categoriesCanExpand && !categoriesExpanded) {
                setCategoriesExpanded(true);
              }
            }}
            onKeyDown={(event) => {
              if (
                categoriesCanExpand &&
                !categoriesExpanded &&
                (event.key === 'Enter' || event.key === ' ')
              ) {
                event.preventDefault();
                setCategoriesExpanded(true);
              }
            }}
            role={categoriesCanExpand && !categoriesExpanded ? 'button' : undefined}
            tabIndex={categoriesCanExpand && !categoriesExpanded ? 0 : undefined}
          >
            {item?.competencies?.map((competency: any, index: number) => (
              <div key={index} className="categoryItem">
                {competency?.competency?.name}
              </div>
            ))}
          </div>
          {(categoriesCanExpand || categoriesExpanded) && (
            <button
              type="button"
              className="scroll-indicator"
              onClick={toggleCategoriesExpanded}
            >
              <Image className="arrow-down" name="down-arrow.svg" />
              <span className="scroll-indicator-text">
                {categoriesExpanded
                  ? t('Click to collapse')
                  : t('Scroll or click to expand')}
              </span>
            </button>
          )}
        </div>
      </div>
      <div className="cartFooter">
        <div>
          <p>
            {monthlySalary.find((el: any) => el.value == item?.salary)?.to_show}
          </p>
          <span>{item?.hourly_rate && `${item?.hourly_rate}/hour`}</span>
        </div>
        <Button
          className="next"
          title="Details"
          t={t}
          onClick={() =>
            navigate(
              isCandidate
                ? `/job/${item?.id}`
                : `/jobs/${item?.slug || item?.id}`
            )
          }
        />
      </div>
    </div>
  );
};

export default JobOpeningCard;
